import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, combineLatest, distinct, distinctUntilChanged, filter, firstValueFrom, map, of, Subscription, switchMap, take } from 'rxjs';

import { environment } from '@environment';

import { SiteConfig } from '@shared/models/site-config';

import { ShiftContextService } from '../../../../services/shift-context.service';
import { InboundLoadService } from '../../../../services/inbound-load.service';
import { InboundLoad, UnloadInboundLoadUpdate } from '../../../../models/inbound-load';
import { toTimestamp } from '../../../../helpers/to-timestamp';
import { timeFormat } from '@shared/helpers/timeFormat';
import { MessageService } from '@shared/services/message-service';
import { DateTime } from 'luxon';
import { parseDuration } from '@shared/helpers/parse-time';
import { ShiftContext } from 'projects/warehousing/src/app/models/shift-context';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';
import { CustomerService } from '@shared/services/customer.service';
import { fromFormControl } from '@shared/helpers/from-form-control';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
    selector: 'app-unload-inbound',
    templateUrl: './unload-inbound.component.html',
    styleUrls: ['./unload-inbound.component.scss']
})
export class UnloadInboundComponent implements OnInit, OnDestroy {
    _load = new BehaviorSubject<InboundLoad>(null);

    @Input() set load(load: InboundLoad) {
        this._load.next(load);
    }

    form = new FormGroup({
        unloadedShiftId: new FormControl<number>(null, Validators.required),
        unloadedDate: new FormControl<Date>(null, Validators.required),
        unloadedTime: new FormControl<string>(null, Validators.required),
        vendorBolNumber: new FormControl<string>(''),
        trailerId: new FormControl<number>(null, Validators.required),
        isRejected: new FormControl<boolean>(false),
        rejectedBolNumber: new FormControl<string>(''),
        redTagFlag: new FormControl<boolean>(false),
        redTagReason: new FormControl<string>(''),
    });

    subscriptions: Subscription[];

    updating: boolean = false;
    supplierBOLRequired: boolean = false;
    requestingBolNumber: boolean = false;
    siteTimezone: string = environment.fallbackTimeZone;
    currentContext: ShiftContext;

    constructor(
        private dialogRef: DynamicDialogRef,
        private config: DynamicDialogConfig,
        private inboundLoadService: InboundLoadService,
        private shiftContextService: ShiftContextService,
        private workedShiftService: WorkedShiftsService,
        private customerService: CustomerService,
        private messageService: MessageService
    ) {
        this.form.setAsyncValidators([
            async (form: typeof this.form) => {
                if (!this._load.value?.arrivedTimestamp?.isValid)
                    return null;

                var unloadDateTime = DateTime.fromJSDate(form.controls.unloadedDate.value)
                    .plus(parseDuration(form.controls.unloadedTime.value ?? '00:00'));

                var arrivedDateTime = this._load.value.arrivedTimestamp;

                return arrivedDateTime > unloadDateTime
                    ? { timeSequence: { valid: false } }
                    : null;
            },
            async (form: typeof this.form) => {
                var unloadDateTime = DateTime.fromJSDate(form.controls.unloadedDate.value);

                var shiftDate = await firstValueFrom(this.workedShiftService.PeekById(form.controls.unloadedShiftId.value));

                const diff = unloadDateTime.diff(shiftDate.date, 'days').days;

                return diff > 1 || diff < 0
                    ? { datediff: { valid: false } }
                    : null;
            }
        ]);
    }

    ngOnInit(): void {
        this._load.next(this.config.data?.load ?? null);
        this.subscriptions = [
            this.shiftContextService.SiteConfig.subscribe(configs => {
                this.siteTimezone = configs
                    .find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)
                    ?.GetValue<string>()
                    ?? environment.fallbackTimeZone;
            }),

            this.shiftContextService.CurrentContext.subscribe(context => {
                this.currentContext = context;
            }),

            combineLatest([
                this._load.pipe(filter(load => !!load)),
                combineLatest([
                    this.shiftContextService.CurrentContext,
                    fromFormControl(this.form.controls.unloadedShiftId).pipe(
                        switchMap(shiftId => shiftId ? this.workedShiftService.FollowById(shiftId) : of(null)),
                        map(shift => shift?.date),
                        filter(date => !!date),
                        distinctUntilChanged((prev, curr) => prev?.equals(curr) ?? false))
                ])
                    .pipe(switchMap(([context, unloadedShiftDate]) => this.customerService.getCustomers(context.site.id, unloadedShiftDate, 'inbound')))
            ]).subscribe(([value, customerAssignments]) => {
                const customer = customerAssignments.customers.find(s => s.id === value.customerId);

                if (customer) {
                    this.form.controls.vendorBolNumber.clearValidators();

                    this.supplierBOLRequired = customer.isCustomerRefRequired;

                    if (this.supplierBOLRequired)
                        this.form.controls.vendorBolNumber.setValidators(Validators.required);

                    this.form.controls.vendorBolNumber.updateValueAndValidity();
                }
            }),

            this._load
                .pipe(switchMap(load => combineLatest([
                    of(load),
                    this.workedShiftService.PeekById(load?.unloadedWorkedShiftId ?? this.currentContext?.id)
                        .pipe(take(1))
                ])))
                .subscribe(([load, initShift]) => {
                    if (load) {
                        this.form.setValue({
                            unloadedShiftId: initShift?.id ?? null,
                            unloadedDate: (load.unloadedTimestamp ?? initShift.date).startOf('day').toJSDate(),
                            unloadedTime: timeFormat((load.unloadedTimestamp ?? DateTime.local()).setZone(this.siteTimezone)),
                            vendorBolNumber: load.vendorBolNumber ?? '',
                            trailerId: load.trailerId ?? null,
                            isRejected: load.isRejected,
                            rejectedBolNumber: load.rejectedBolNumber ?? '',
                            redTagFlag: load.redTagFlag,
                            redTagReason: load.redTagReason ?? '',
                        });
                    }
                })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    onUnloadedShiftDateChanged(date: DateTime): void {
        if (date)
            this.form.controls.unloadedDate.setValue(date.toJSDate());
    }

    getBolNumber(): void {
        this.requestingBolNumber = true;

        if (this.currentContext) {
            this.inboundLoadService.getNextBolNumber(this.currentContext.site.id).subscribe({
                next: bolNum => {
                    this.form.controls.vendorBolNumber.setValue(bolNum);
                    this.requestingBolNumber = false;
                },
                error: _err => {

                    this.requestingBolNumber = false;
                }
            });
        }
    }

    unloadLoad(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();

            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Unload Load',
                detail: 'Please correct the listed errors.'
            });

            return;
        }

        const update: UnloadInboundLoadUpdate = {
            unloadedWorkedShiftId: this.form.controls.unloadedShiftId.value,
            unloadedTimestamp: toTimestamp(this.form.controls.unloadedDate.value, this.form.controls.unloadedTime.value, this.siteTimezone),
            vendorBolNumber: this.form.controls.vendorBolNumber.value,
            trailerId: this.form.controls.trailerId.value,
            isRejected: this.form.controls.isRejected.value,
            rejectedBolNumber: this.form.controls.rejectedBolNumber.value,
            redTagFlag: this.form.controls.redTagFlag.value,
            redTagReason: this.form.controls.redTagReason.value,
        };

        this.updating = true;
        this.inboundLoadService.unloadInboundLoad(this._load.value.id, update)
            .subscribe({
                next: (load) => {
                    this.updating = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Load Unloaded',
                        detail: `Load ${this._load.value.vendorBolNumber} has been unloaded.`
                    });

                    this.dialogRef.close(load);
                },
                error: _ => {
                    this.updating = false;
                }
            });
    }
}
